import { Component, Input, OnInit } from '@angular/core';
import { FormGroupDirective } from '@angular/forms';
import { UploadDokumentArt } from '../../../shared/enum';
import { FileMimeType } from '../../../shared/mimetype';
import { TranslateService } from '@ngx-translate/core';
import { PopupService } from 'src/app/service/popup.service';
import { IFile } from '../../models/file';
import { timestampToDateKey } from '../../../shared/utils';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss']
})
export class FileUploadComponent implements OnInit {

  @Input()
  parentFormGroupDirective: FormGroupDirective;

  @Input()
  dokumentArt: UploadDokumentArt;

  @Input()
  required = false;

  @Input()
  multiple = true;

  @Input()
  selectedFilesTotal: IFile[];

  @Input()
  acceptedFileTypes = 'application/pdf, image/jpeg, image/png, image/gif, application/vnd.openxmlformats-officedocument.wordprocessingml.document' +
    ', application/msword, text/plain, application/msword';

  selectedFiles: IFile[] = [];

  fileSizeTotalMax = 157286400;
  fileSizeMax = 10485760;

  errorFileSizeTotal = 'fileSizeTotal';
  errorFileSize = 'fileSize';
  errorInvalidFileType = 'invalidFileType';

  dokumentArten = UploadDokumentArt;

  anzahlFilesExpansion = 2;

  constructor(public translateService: TranslateService, public popupService: PopupService) { }

  ngOnInit(): void {
  }

  get dokumentArtLabel() {
    return this.dokumentArt === UploadDokumentArt.Antrag ? 'Antrag' :
      this.dokumentArt === UploadDokumentArt.BmNachweis ? 'BM-Nachweis' :
        this.dokumentArt === UploadDokumentArt.Kaskobesichtigung ? 'KFZ-Besichtigung/Händler KV' :
          this.dokumentArt === UploadDokumentArt.Sonderausstattung ? 'Aufstellung Sonderausstattung' :
            this.dokumentArt === UploadDokumentArt.Sonstige ? 'Sonstige Dokumente' : null;
  }

  get dokumentArtInfo() {
    let key = 'NOKEY';
    switch (this.dokumentArt) {
      case UploadDokumentArt.BmNachweis:
        key = 'BMNACHWEISINFO';
        break;
      case UploadDokumentArt.Kaskobesichtigung:
        key = 'KASKOBESICHTIGUNGNACHWEISINFO';
        break;
      case UploadDokumentArt.Sonderausstattung:
        key = 'AUFSTELLUNGNACHWEISINFO';
        break;
    }
    return this.translateService.get(key);
  }

  get valid() {
    return (!this.required || this.required && this.selectedFiles.length) && !this.selectedFiles.some(x => x.Errors.size);
  }

  get acceptedFileExtensions() {
    return this.acceptedFileTypes.split(',').map(x => FileMimeType[x.trim()])
      .filter((value, index, array) =>
        array.indexOf(value) === index
      ).join(', ');
  }

  get fileSizeTotal() {
    return this.selectedFilesTotal.map(x => x.Size).reduce((x, y) => x + y, 0);
  }

  get uploadedFiles() {
    return this.selectedFiles.filter(x => x.FileId);
  }

  get addedFiles() {
    return this.selectedFiles.filter(x => !x.FileId);
  }

  get uploadedFilesDateGroups() {
    return this.uploadedFiles.reduce(
      (m, e) => m.set(timestampToDateKey(e.Ts), [...m.get(timestampToDateKey(e.Ts)) || [], e]),
      new Map()
    );
  }

  updateFileSizeTotal(removedFiles: IFile[]) {
    const selectedFilesTotal = this.selectedFilesTotal.filter(
      x => !removedFiles.some(y => y === x));
    for (let index = 0; index < selectedFilesTotal.length; index++) {
      const fileSize = selectedFilesTotal.slice(0, index + 1)
        .map((x) => x.Size)
        .reduce((x, y) => x + y);
      if (fileSize <= this.fileSizeTotalMax) {
        selectedFilesTotal[index].Errors.delete(this.errorFileSizeTotal);
      }
    }
  }

  addSelectedFiles(files: IFile[]) {
    if (!this.multiple) {
      this.selectedFiles = [];
    }
    for (let index = 0; index < files.length; index++) {
      const errors: Map<string, boolean> = new Map<string, boolean>();
      const file = files[index];
      const fileSize = files.slice(0, index + 1)
        .map((x) => x.Size)
        .reduce((x, y) => x + y);
      if (this.fileSizeTotal + fileSize >= this.fileSizeTotalMax) {
        errors.set(this.errorFileSizeTotal, true);
      }
      if (file.Size >= this.fileSizeMax) {
        errors.set(this.errorFileSize, true);
      }
      if (!file.Type || this.acceptedFileTypes.indexOf(file.Type) === -1) {
        errors.set(this.errorInvalidFileType, true);
      }
      file.Errors = errors;
      this.selectedFiles.push(file);
    }
  }

  removeFiles(files: IFile[]) {
    for (const file of files) {
      this.removeFile(file);
    }
  }

  removeFile(file: IFile) {
    this.updateFileSizeTotal([file]);
    const index = this.selectedFiles.findIndex(x => x === file);
    this.selectedFiles.splice(index, 1);
  }

  addFiles(event) {
    const files = Array.from(event as FileList).map(x => ({
      Name: x.name,
      File: x,
      DokumentArt: this.dokumentArt,
      Size: x.size,
      Type: x.type
    } as IFile));
    this.addSelectedFiles(files);
  }

  onFileDropped(event) {
    this.addFiles(event);
  }

  onFileSelected(event) {
    this.addFiles(event.target.files);
  }
}
